import React, { useState, useEffect } from "react";
import { Button } from "@bspk/ui/Button";
import PinInputGroup from "./PinInputGroup";
import { useBrand } from "../../theme/BrandProvider";
import { useNavigate } from "react-router-dom";
import { useMetroConfig } from "../../contexts/MetroConfigContext";
import { interpolateAgentTemplate } from "../../utils/templateInterpolation";
interface VerificationFormProps {
  onVerify: (pin: string) => void;
  isLoading?: boolean;
  error?: string;
  onEdit?: () => void;
}

const VerificationForm: React.FC<VerificationFormProps> = ({
  onVerify,
  isLoading = false,
  error,
  onEdit,
}) => {
  const brands = useBrand();
  const navigate = useNavigate();
  const { metroConfig } = useMetroConfig();
  const pinPage = (metroConfig?.data as { configs?: { PinPage?: Record<string, string> } } | undefined)?.configs?.PinPage;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  const [pin, setPin] = useState("");
  const [hasError, setHasError] = useState(false);

  const handlePinChange = (newPin: string) => {
    setPin(newPin);
    // Notify parent to clear external error when user edits
    if (onEdit) {
      onEdit();
    }
    if (hasError) {
      setHasError(false);
    }
  };

  // Helper function to get clean PIN value (remove position encoding)
  const getCleanPin = (pin: string) => {
    return pin.replace(/·/g, '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const cleanPin = getCleanPin(pin);
    if (cleanPin.length !== 8) {
      setHasError(true);
      return;
    }

    onVerify(cleanPin);
  };

  const cleanPin = getCleanPin(pin);
  const isValid = cleanPin.length === 8;
  const hasExternalError = Boolean(error ?? false);
  const showError = hasError ? true : hasExternalError;

  const renderVerifyButton = (): React.ReactNode => {
    let stateClasses: string;
    if (isValid) {
      stateClasses = "bg-blue-600 text-white hover:bg-blue-700";
    } else if (showError) {
      stateClasses = "bg-red-100 text-red-600 cursor-not-allowed";
    } else {
      stateClasses = "bg-gray-200 text-gray-400 cursor-not-allowed";
    }

    let backgroundColor: string;
    if (isValid) {
      backgroundColor = brands.theme.colors.primary;
    } else if (showError) {
      backgroundColor = "#FEE2E2"; // light red for error
    } else {
      backgroundColor = "#0000000f"; // light gray for default
    }

    return (
      <Button
        type="submit"
  label={interpolateAgentTemplate(pinPage?.Button1, {}) || 'Verify'}
  aria-label={interpolateAgentTemplate(pinPage?.Button1, {}) || 'Verify'}
        size="medium"
        width="fill"
        variant={isValid ? "primary" : "tertiary"}
        disabled={!isValid || isLoading}
        className={`
          w-full rounded
          ${stateClasses}
        `}
        style={{
          background: backgroundColor,
        }}
      />
    );
  };

  return (
    <div
      className={`
      max-w-md mx-auto px-4
      ${isMobile ? "pt-6" : "pt-12 pb-12"}
    `}
    >
      <div className="text-center space-y-6">
        {/* Title Section */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-gray-800 leading-6">
            {interpolateAgentTemplate(pinPage?.Header1, {})}
          </h2>
          <h1 className="text-[28px] leading-[36px] font-semibold text-gray-900">
            {interpolateAgentTemplate(pinPage?.Header2, {})}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <p className="text-base font-medium text-gray-600 leading-5">
              {interpolateAgentTemplate(pinPage?.para1, {})}
            </p>

            <PinInputGroup
              value={pin}
              onChange={handlePinChange}
              disabled={isLoading}
              error={showError}
              length={8}
            />

            {showError && (
              <p className="text-sm text-red-600">
                {error ?? "Please enter a valid 8-digit PIN"}
              </p>
            )}
          </div>

          <div
            className={`space-y-4${!isMobile ? " px-8" : ""}`}
            style={
              !isMobile
                ? { paddingLeft: "2rem", paddingRight: "2rem" }
                : undefined
            }
          >
            <p
              className="text-sm text-gray-600 leading-5"
              style={
                !isMobile ? { paddingTop: 6, paddingBottom: 14 } : undefined
              }
            >
              If you have any questions, please{" "}
              <button
                type="button"
                className="text-blue-600 hover:text-blue-800 underline"
                onClick={() => navigate("/support")}
              >
                contact support
              </button>{" "}
              .
            </p>

            {isMobile && (
              <div className="fixed inset-x-0 bottom-0 border-t border-gray-300 pt-6 pb-6 mx-6">
                {renderVerifyButton()}
              </div>
            )}

            {!isMobile && <>{renderVerifyButton()}</>}
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerificationForm;
